import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { phiSafeText } from '../../lib/validation'
import { offlineStore } from '../../lib/offline/store'
import { safeLogger } from '../../lib/logger'
import { AlertCircle, Save, Wifi } from 'lucide-react'
import { getVisitChecklist } from '../../lib/services/visits'
import { toBackendResponseValue, upsertResponse } from '../../lib/services/responses'
import { authFailureMessage, isAuthFailure, shouldRetryRequest } from '../../lib/errors'
import { useAuth } from '../../context/AuthContext'
import { buildDraftId } from '../../lib/offline/draft-id'
import { FieldPanel, FieldResponseGroup, type FieldResponseOption } from '../../components/field/FieldAssessmentControls'

type ChecklistResponseValue = 'Compliant' | 'Non-Compliant' | 'Not Applicable'

const GUIDED_CHECKLIST_OPTIONS: Array<FieldResponseOption<ChecklistResponseValue>> = [
    { value: 'Compliant', label: 'Compliant', description: 'Meets standard', tone: 'success' },
    { value: 'Non-Compliant', label: 'Non-Compliant', description: 'Needs follow-up', tone: 'danger' },
    { value: 'Not Applicable', label: 'N/A', description: 'Not in scope', tone: 'neutral' },
]

/**
 * Response Entry Component [FE.2.1/FE.2.2]
 * Features:
 * - Mobile-first UI
 * - PHI Scanning via Zod
 * - Automatic Offline Drafts
 */
export function ResponseEntryPage() {
    const { visitId, standardId } = useParams()
    const navigate = useNavigate()
    const { signOut } = useAuth()

    const [value, setValue] = useState<ChecklistResponseValue>('Compliant')
    const [notes, setNotes] = useState('')
    const [isPhiConfirmed, setIsPhiConfirmed] = useState(false)
    const [phiWarning, setPhiWarning] = useState<string | null>(null)
    const [isSaving, setIsSaving] = useState(false)
    const [saveError, setSaveError] = useState<string | null>(null)
    const [isAuthSaveError, setIsAuthSaveError] = useState(false)

    const { data: checklist, error: checklistError, isLoading: isChecklistLoading } = useQuery({
        queryKey: ['visit-checklist', visitId],
        queryFn: () => getVisitChecklist(visitId!),
        enabled: Boolean(visitId),
        retry: shouldRetryRequest,
    })

    const selectedItem = checklist?.find((item) => item.template_id === standardId)
    const draftId = visitId && standardId ? buildDraftId(visitId, standardId) : null

    // 1. Scan for PHI on notes change
    useEffect(() => {
        const result = phiSafeText.safeParse(notes)
        if (!result.success) {
            setPhiWarning(result.error.issues[0].message)
        } else {
            setPhiWarning(null)
            setIsPhiConfirmed(false)
        }
    }, [notes])

    // 2. Auto-save draft to IDB [FE.2.1]
    useEffect(() => {
        if (draftId) {
            offlineStore.saveDraft(draftId, {
                response_value: toBackendResponseValue(value),
                notes,
            })
        }
    }, [value, notes, draftId])

    const handleSave = async () => {
        if (phiWarning && !isPhiConfirmed) {
            alert('Please confirm the PHI safety flag before saving.')
            return
        }

        setIsSaving(true)
        setSaveError(null)
        setIsAuthSaveError(false)
        try {
            if (!visitId || !standardId) {
                throw new Error('Missing visit or checklist template context')
            }

            await upsertResponse({
                visitId,
                templateId: standardId,
                responseValue: toBackendResponseValue(value),
                notes,
            })

            if (draftId) {
                await offlineStore.removeDraft(draftId)
            }
            setIsSaving(false)
            safeLogger.logEvent('response_saved', { status: 200 })
            navigate(`/visits/${visitId}`)
        } catch (error) {
            setIsSaving(false)
            safeLogger.logError('response_save_failed', crypto.randomUUID())
            if (isAuthFailure(error)) {
                setSaveError(authFailureMessage(error))
                setIsAuthSaveError(true)
                return
            }
            setSaveError('Save failed. Draft remains local and can be retried when online.')
        }
    }

    return (
        <div className="mx-auto max-w-4xl space-y-5 pb-20">
            <header className="app-surface p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                        <p className="text-xs font-bold uppercase text-teal-700">Checklist Response</p>
                        <h1 className="mt-1 font-brand text-xl font-bold text-[var(--brand-deep)]">Template {standardId}</h1>
                        <p className="mt-2 text-sm text-slate-700">{selectedItem?.question_text || 'Checklist response entry'}</p>
                    </div>
                    <div className="rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-bold text-emerald-700">
                        <span className="inline-flex items-center gap-1">
                            <Wifi size={14} />
                            Sync Active
                        </span>
                    </div>
                </div>
                {/* Tab indicators removed — Checklist/Evidence were non-functional visual labels */}
            </header>
            {isChecklistLoading && (
                <div className="app-surface p-3 text-slate-600">
                    <p className="text-sm">Loading checklist context...</p>
                </div>
            )}
            {Boolean(checklistError) && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-red-700">
                    <p className="text-sm font-medium">
                        {isAuthFailure(checklistError) ? 'Session validation failed for checklist' : 'Failed to load checklist'}
                    </p>
                    <p className="text-xs">
                        {isAuthFailure(checklistError) ? authFailureMessage(checklistError) : (checklistError as Error).message}
                    </p>
                </div>
            )}

            <FieldPanel title="Field Response" eyebrow="Guided checklist" meta={<span className="text-[10px] font-bold text-slate-400">Auto-saves draft</span>}>
                {/* Response Value */}
                <FieldResponseGroup
                    label="Checklist result"
                    value={value}
                    options={GUIDED_CHECKLIST_OPTIONS}
                    onChange={setValue}
                />

                {/* Notes with PHI Warning */}
                <div className="mt-5 space-y-2">
                    <label className="block text-sm font-bold text-slate-700">Notes / Evidence Description</label>
                    <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        className="h-32 w-full rounded-lg border border-neutral-200 p-3 text-sm outline-none focus:ring-2 focus:ring-blue-200"
                        placeholder="Describe your findings..."
                    />

                    {phiWarning && (
                        <div className="flex flex-col space-y-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800">
                            <div className="flex items-center space-x-2">
                                <AlertCircle size={16} />
                                <span className="text-xs font-bold">{phiWarning}</span>
                            </div>
                            <label className="flex items-center space-x-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={isPhiConfirmed}
                                    onChange={(e) => setIsPhiConfirmed(e.target.checked)}
                                    className="rounded text-blue-600"
                                />
                                <span className="text-xs">I confirm these notes contain no direct PHI.</span>
                            </label>
                        </div>
                    )}
                </div>

                <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="btn-primary flex w-full items-center justify-center space-x-2 py-3 disabled:opacity-50"
                >
                    <Save size={18} />
                    <span>{isSaving ? 'Syncing...' : 'Complete & Save'}</span>
                </button>
                {saveError && (
                    <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-red-700">
                        <p className="text-sm">{saveError}</p>
                        {isAuthSaveError && (
                            <button
                                className="mt-1 text-xs underline"
                                onClick={async () => {
                                    await signOut()
                                    navigate('/login')
                                }}
                            >
                                Sign in again
                            </button>
                        )}
                    </div>
                )}
            </FieldPanel>

            <p className="text-center text-xs text-slate-500">
                Drafts are automatically saved to your encrypted local store.
            </p>
        </div>
    )
}
